<template>
  <div class="p-page-wrapper">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
          @submit="searchContacts"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('contact.createContact') }}
        </v-btn>
        <v-btn
          color="primary"
          class="p-btn-text"
          text
          large
          @click="labelsPanel = true"
        >
          {{ $t('label.manageLabels') }}
          <v-icon class="ml-2">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col>
        <v-data-table
            :headers="tableHeaders"
            :items="contactsTableItemsMapped"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            :server-items-length="contactsTableTotalCount"
            no-data-text=""
            hide-default-footer
            class="p-no-borders"
            @page-count="pageCount = $event"
            @click:row="openViewDialog"
            @update:page="reloadContacts"
          >
            <template v-slot:[`item.displayName`]="{ item }">
              <p-contact-item
                :contact="item"
                :is-display-name-with-company="false"
                class="px-0"
                font-bold
              ></p-contact-item>
            </template>
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('contact.table.changedTooltip') }}
            </v-tooltip>
          </template>
            <template v-slot:[`item.labels`]="{ item }">
              <v-chip
                v-for="label of item.labels"
                :key="label"
                small
                outlined
                label
              >
                {{ (contactLabelById(label) || {}).name }}
              </v-chip>
            </template>
            <template v-slot:[`item.displayEmails`]="{ item }">
              <div class="my-2">
                <p v-for="(email, index) in item.displayEmails"
                   :key="`email-${index}`"
                   class="my-1 text-no-wrap">
                  {{ email }}
                </p>
              </div>
            </template>
            <template v-slot:[`item.displayPhones`]="{ item }">
                <div class="my-2">
                  <p v-for="(phone, index) in item.displayPhones"
                     :key="`phone-${index}`"
                     class="my-1 text-no-wrap">
                    {{ phone }}
                  </p>
                </div>
            </template>
            <template v-slot:[`item.iceContact`]="{ item }">
              <div class="my-2">
                <p v-if="item.iceContact && item.iceContact.name" class="text-no-wrap mb-0 p-bold-text">{{ item.iceContact.name }}</p>
                <p v-if="item.iceContact && item.iceContact.phone" class="text-no-wrap">{{ item.iceContact.phone }}</p>
              </div>
            </template>
            <template v-slot:[`item.bonusProgram`]="{ item }">
              <div class="my-2">
                {{ item.bonusProgram }}
              </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <div class="p-table-row-actions p-visible-on-row-hover">
                <template v-if="hasAccess()">
                  <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
                  <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
                  <contact-actions-menu :contact="item" @merged="onMerged"></contact-actions-menu>
                </template>
                <template v-else>
                  <p-btn-icon type="view" @click="openEditDialog(item)"></p-btn-icon>
                </template>
              </div>
            </template>
            <template v-slot:[`no-data`]>
              <p-no-data
                v-if="!contactsLoading && shortContacts.length === 0"
                img="contact.png"
                class="p-no-data-mt"
                :text="$t('contact.noContacts')"
                :btn-text="$t('contact.createContact')"
                :hide-btn="!hasAccess()"
                @btn-click="openEditDialog(null)"
              ></p-no-data>
              <div v-else-if="!contactsLoading && contactsTableItems.length === 0">
                {{ $t('noResults') }}
              </div>
            </template>
          </v-data-table>

        <v-pagination
          v-if="pageCount > 0"
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-10"
        ></v-pagination>
      </v-col>
    </v-row>

    <contact-view-dialog
      :dialog.sync="viewDialog"
      :contact="selectedContact"
    ></contact-view-dialog>

    <contact-edit-dialog
      :dialog.sync="editDialog"
      :contact="editContact"
    ></contact-edit-dialog>

    <p-delete-dialog
      :dialog.sync="deleteDialog"
      :item="deletedContact"
      :item-data="deletedContactData"
      :load-relations="loadRelations"
      :delete-item="deleteContact"
      @itemDeleted="onItemDeleted"
    ></p-delete-dialog>

    <contact-labels-panel
      :panel.sync="labelsPanel"
    ></contact-labels-panel>
  </div>
</template>


<script>
import eventBus from '@/eventBus'

import { mapActions, mapGetters } from 'vuex'

import PContactItem from '@/components/PContactItem'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'

import ContactViewDialog from './ContactViewDialog'
import ContactEditDialog from './ContactEditDialog'
import ContactActionsMenu from './ContactActionsMenu'
import ContactLabelsPanel from './ContactLabelsPanel'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PContactItem,
    PSearchInput,
    PNoData,
    PBtnIcon,
    PDeleteDialog,
    ContactViewDialog,
    ContactEditDialog,
    ContactActionsMenu,
    ContactLabelsPanel
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      contactsLoading: true,
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      viewDialog: false,
      editDialog: false,
      editContact: null,
      deleteDialog: false,
      deletedContact: null,
      deletedContactData: null,
      labelsPanel: false,
      selectedContact: {}
    }
  },


  computed: {
    ...mapGetters('contact', ['shortContacts', 'contactsTableItems', 'contactsTableTotalCount']),
    ...mapGetters('contactLabel', ['contactLabels', 'contactLabelById']),
    ...mapGetters('changes', ['isItemChanged']),

    contactsTableItemsMapped () {
      return this.contactsTableItems.map(item => {
        return {
          ...item,
          changed: this.isItemChanged(item.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('contact.table.name'), value: 'displayName', sortable: false },
        { text: '', value: 'changed' },
        { text: this.$t('contact.table.company'), value: 'company', sortable: false },
        { text: this.$t('contact.table.emails'), value: 'displayEmails', sortable: false },
        { text: this.$t('contact.table.phones'), value: 'displayPhones', sortable: false },
        { text: this.$t('contact.table.address'), value: 'fullAddress', width: 300, sortable: false },
        { text: this.$t('contact.table.labels'), value: 'labels', width: 300, sortable: false },
        { text: this.$t('contact.table.note'), value: 'note', sortable: false },
        { text: this.$t('contact.table.iceContact'), value: 'iceContact', sortable: false },
        { text: this.$t('contact.table.bonusProgram'), value: 'bonusProgram', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.contactLabels.map(label => {
        return label.name
      })

      for (const contact of this.shortContacts) {
        if (contact.displayName && items.find(x => x === contact.displayName) == null) {
          items.push(contact.displayName)
        }
      }

      return items
    }
  },


  methods: {
    ...mapActions('contact', ['loadContactsTableItems', 'loadShortContacts', 'loadContactById', 'loadRelations', 'deleteContact']),
    ...mapActions('contactLabel', ['loadContactLabels']),

    initEventBus () {
      eventBus.$on('merged', (merged) => {
        this.onMerged(merged)
      })

      eventBus.$on('goToEntity', async (entityTitle) => {
        await this.searchContacts(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.reloadContacts(1)
      })
    },

    openViewDialog (contact) {
      this.selectedContact = contact
      this.viewDialog = true
    },

    closeViewDialog () {
      this.viewDialog = false
    },

    openEditDialog (contact) {
      this.editContact = contact
      this.editDialog = true
    },

    async openDeleteDialog (contact) {
      try {
        if (contact) {
          this.deletedContact = await this.loadContactById(contact.id)
          this.deletedContactData = {
            type: this.$t('entity.contact'),
            title: `${this.deletedContact.displayNameWithCompany}`
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('contact.delete.contactDeleted'))
    },

    async searchContacts (value) {
      await this.reloadContacts(1, value)
    },

    async reloadContacts (page, search) {
      try {
        this.startPageLoading()
        this.page = page || this.page
        this.contactsLoading = true
        await this.loadContactsTableItems({
          skip: (this.page - 1) * this.itemsPerPage,
          take: this.itemsPerPage,
          search: search || this.search
        })
        this.contactsLoading = false
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    onMerged (merged) {
      if (merged) {
        this.initData()
        this.closeViewDialog()
      }
    },

    async initData () {
      try {
        this.startPageLoading()

        await Promise.all([
          this.loadContactLabels(),
          this.loadShortContacts(),
          this.loadContactsTableItems({ take: this.itemsPerPage })
        ])
        this.contactsLoading = false
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    }
  },

  async created () {
    this.setPageTitle(this.$t('nav.contacts'))
    this.initData()
    this.initEventBus()
    if (this.$router.currentRoute.params?.goto) {
      await this.searchContacts(this.$router.currentRoute.params?.goto)
    }
  }
}
</script>
